import { can } from "modules/settings/accessRoles";
import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Skeleton from "@mui/material/Skeleton";
import { spacing } from "@mui/system";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Box as BoxIcon, Map as MapIcon, User as UserIcon, Zap as ZapIcon } from "react-feather";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import StandardHeader from "../../components/global/StandardHeader";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import {
  getAllAccountStatusCodes,
  getAllCustomerTypes,
  getAllPropertyPreviews,
  getAllUnitTypes,
} from "../../modules/apl/api/actions";
import GenericStatusTag from "../../modules/apl/components/GenericStatusTag";
import { get as getDevelopers } from "../../modules/developer/api/actions";
import { get as getOwners } from "../../modules/owner/api/actions";
import {
  getAllCommonChargeRelatedDataByPropertyId,
  getByUuid as getPropertyByUuid,
  getPropRiderRatesByPropByUuid,
  getResolvedRiderRatesByPropertyUuid,
} from "../../modules/property/api/actions";
import PropertyDetailCard from "../../modules/property/components/DetailCard";
import {
  get as getPropertyManagers,
  getById as getPropertyManagerById,
} from "../../modules/propertyManager/api/actions";
import PropertyManagerDetailCard from "../../modules/propertyManager/components/DetailCard";
import { get as getPropertyManagerStatuses } from "../../modules/propertyManagerStatus/api/actions";
import { get as getPropertyStatuses } from "../../modules/propertyStatus/api/actions";
import { getByPropertyId as getPropertyUtilityByPropertyId } from "../../modules/propertyUtility/api/actions";
import { getByPropertyId as getPropertyGLCodeByPropertyId } from "../../modules/propertyGLCode/api/actions";
import PropertyUtilityDetailCard from "../../modules/propertyUtility/components/DetailCard";
import PropertyGLCodeDetailCard from "../../modules/propertyGLCode/components/DetailCard";
import { getByPropertyId as getUnitsByPropertyId } from "../../modules/unit/api/actions";
import { get as getVendors } from "../../modules/vendor/api/actions";
import PropertyDashboardTabedTables from "./PropertyManagerTabbedTabels";

const Divider = styled(MuiDivider)(spacing);
const IndividualPropertyPage = (props) => {
  const params = useParams();
  const [propertyManagerModalOpen, setPropertyManagerModalOpen] = useState(false);
  const [accountingIdModalOpen, setAccountingIdModalOpen] = useState(false);
  
  const useStyles = makeStyles((theme) => ({
    title: {
      flexGrow: 1,
    },
    percentDif: {
      textAlign: "right",
      color: "green",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: theme.palette.common.white,
    },
    loadingIcon: {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    StatsIcon: {
      width: "32px",
      height: "32px",
      color: theme.palette.secondary.main,
      position: "absolute",
      right: "16px",
      top: "32px",
    },
    rightSideHeader: {
      display: "flex",
      alignItems: "flex-end",
      flexDirection: "row",
      justifyContent: "flex-end",
    },
    rightSideHeaderSubMenuToolBar: {
      display: "flex",
      alignItems: "flex-end",
      flexDirection: "row",
      justifyContent: "flex-end",
      margin: "0px",
      padding: "0px",
    },
    SubMenuToolBarIcon: {
      width: "25px",
      height: "25px",
      paddingBotom: "5px",
    },
    orange: {
      //color: theme.palette.getContrastText(deepOrange[500]),
    },
    UserBoxText: {
      textAlign: "center",
    },
    UserProfileDetailsButton: {
      padding: "5px",
    },
    AvatarText: {
      marginTop: "20%",
      height: "128px",
      width: "128px",
      textAlign: "center",
      alignSelf: "center",
    },
    AvatarIcon: {
      display: "inline-block",
      height: "128px",
      width: "128px",
      backgroundColor: "gray",
      borderColor: theme.palette.primary.main,
      borderStyle: "solid",
    },
    ChartWrapper: {
      height: "200px",
      maxHeight: "200px",
      minHeight: "200px",
    },
    AccountInfoGrid: {
      height: "400px",
      maxHeight: "400px",
      minHeight: "400px",
    },
  }));

  const classes = useStyles();
  const Button = styled(MuiButton)(spacing);

  const onUpdateUnits = () => {
    let propertyId = params.id;
    props.getUnitsByPropertyId(propertyId, null);
  };

  useEffect(() => {
    let propertyUuid = params.uuid;
    let propertyId = params.id;
    let propertyManagerId = params.propertyManagerId;

    props.getPropRiderRatesByPropByUuid(propertyUuid);
    props.getResolvedRiderRatesByPropertyUuid(propertyUuid, new Date().toISOString().slice(0, 10));
    props.getAllCommonChargeRelatedDataByPropertyId(propertyId);
    props.getPropertyByUuid(propertyUuid);
    props.getPropertyUtilityByPropertyId(propertyId);
    props.getPropertyGLCodeByPropertyId(propertyId);
    props.getPropertyManagerById(propertyManagerId);
    props.getUnitsByPropertyId(propertyId, null);
    props.getAllAccountStatusCodes();
    props.getAllCustomerTypes();
    props.getAllPropertyPreviews();
    props.getOwners();
    props.getVendors();
    props.getDevelopers();
    props.getPropertyManagers();
    props.getPropertyStatuses();
    props.getPropertyManagerStatuses();
    props.getAllUnitTypes();
  }, [params.uuid, params.id, params.propertyManagerId]);

  // Header Stats Section for the right side of the header
  const HeaderStatsSection = () => {
    const propertyUtility = props.propertyUtilities.data.propertyUtility;
    const vendorName = propertyUtility?.vendorName;
    const vendorUuid = propertyUtility?.vendorUuid;

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: 2,
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%'
      }}>
        {/* Accounting ID */}
        <Box sx={{ 
          textAlign: 'center',
          minWidth: { sm: '120px' }
        }}>
          {!props.propertyGLCode.data.propertyGLCode ? (
            <Skeleton variant="rect" width={100} height={40} />
          ) : (
            <>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'fontWeightRegular',
                  color: 'primary.main',
                  mb: 0.5,
                  cursor: can(props.user.details, "accountingTools") ? 'pointer' : 'default',
                  '&:hover': can(props.user.details, "accountingTools") ? {
                    textDecoration: 'underline'
                  } : {}
                }}
                onClick={() => {
                  if (can(props.user.details, "accountingTools")) {
                    setAccountingIdModalOpen(true);
                  }
                }}
              >
                {props.propertyGLCode.data.propertyGLCode.glCode || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Accounting ID
              </Typography>
            </>
          )}
        </Box>

        {/* Unit Count */}
        <Box sx={{ 
          textAlign: 'center',
          minWidth: { sm: '120px' }
        }}>
          {!props.properties.data.property ? (
            <Skeleton variant="rect" width={100} height={40} />
          ) : (
            <>
              <Typography variant="h4" sx={{ 
                fontWeight: 'fontWeightRegular',
                color: 'primary.main',
                mb: 0.5
              }}>
                {props.units.data.unitsList.items.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <BoxIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Units
              </Typography>
            </>
          )}
        </Box>

        {/* Property Manager */}
        <Box sx={{ 
          textAlign: 'center',
          minWidth: { sm: '140px' }
        }}>
          {!props.propertyManagers.data.propertyManager ? (
            <Skeleton variant="rect" width={120} height={40} />
          ) : (
            <>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'fontWeightMedium',
                  color: 'primary.main',
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={() => setPropertyManagerModalOpen(true)}
              >
                {props.propertyManagers.data.propertyManager.name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <UserIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Manager
              </Typography>
            </>
          )}
        </Box>

        {/* Vendor */}
        <Box sx={{
          textAlign: 'center',
          minWidth: { sm: '140px' }
        }}>
          {!propertyUtility ? (
            <Skeleton variant="rect" width={120} height={40} />
          ) : (
            <>
              {vendorName && vendorUuid ? (
                <Typography
                  component={Link}
                  to={`/vendor/${vendorUuid}`}
                  variant="h6"
                  sx={{
                    fontWeight: 'fontWeightMedium',
                    color: 'primary.main',
                    mb: 0.5,
                    textDecoration: 'none',
                    display: 'block',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {vendorName}
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'fontWeightMedium',
                    color: 'error.main',
                    mb: 0.5
                  }}
                >
                  Missing
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                <ZapIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Vendor
              </Typography>
            </>
          )}
        </Box>

        {/* Total Square Footage */}
        <Box sx={{
          textAlign: 'center',
          minWidth: { sm: '120px' }
        }}>
          {!props.properties.data.property ? (
            <Skeleton variant="rect" width={100} height={40} />
          ) : (
            <>
              <Typography variant="h4" sx={{
                fontWeight: 'fontWeightRegular',
                color: 'primary.main',
                mb: 0.5
              }}>
                {props.properties.data.property.totalSqFt?.toLocaleString() || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <MapIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Sq Ft
              </Typography>
            </>
          )}
        </Box>

        {/* Remote Status */}
        <Box sx={{
          textAlign: 'center',
          minWidth: { sm: '100px' }
        }}>
          {!props.properties.data.property ? (
            <Skeleton variant="rect" width={80} height={40} />
          ) : (
            <>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: 0.5
              }}>
                <Chip 
                  label={props.properties.data.property.isRemote ? 'REMOTE' : 'ON-SITE'}
                  size="small"
                  sx={{
                    backgroundColor: props.properties.data.property.isRemote ? 'warning.light' : 'success.light',
                    color: props.properties.data.property.isRemote ? 'warning.contrastText' : 'success.contrastText',
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Location Type
              </Typography>
            </>
          )}
        </Box>

        {/* Site Map */}
        {props.properties.data.property?.sitePlanSignedUrl && (
          <Box sx={{
            textAlign: 'center',
            minWidth: { sm: '100px' }
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mb: 0.5
            }}>
              <IconButton
                size="small"
                onClick={() => window.open(props.properties.data.property.sitePlanSignedUrl, '_blank')}
                sx={{
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.main'
                  }
                }}
              >
                <MapIcon size={20} />
              </IconButton>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Site Map
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  var pageTitle = "Ubil+ Loading Property...";
  var titleComponent = null;
  // Sets the tab name based on the loaded account being worked on
  if (props.properties.data.property.id) {
    pageTitle = props.properties.data.property.name;
    titleComponent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="span">
          {props.properties.data.property.name}
        </Typography>
        {props.properties.data.property.status && (
          <Chip 
            label={props.properties.data.property.status.description || 'ACTIVE'}
            size="small"
            sx={{
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />
        )}
      </Box>
    );
  }
  document.title = pageTitle;

  return (
    <NavFrame page={"IndividualPropertyPage"}>
      <StandardHeader
        title={titleComponent || pageTitle || "Property Management"}
        appPath={"Property Manager"}
        rightSideToolBar={<HeaderStatsSection />}
      />
      {props.properties.data.property.outage && (
        <div>
          <Grid item direction="column" container spacing={1}>
            <GenericStatusTag
              customStyles={{ backgroundColor: "red" }}
              title={`Outage Reported as of: ${moment(
                props.properties.data.property.outage.outageStart
              ).format("L LT")}`}
            />
          </Grid>
        </div>
      )}

      {!props.propertyUtilities.propertyUtilityIsLoading &&
        props.propertyUtilities.data.propertyUtility &&
        !props.propertyUtilities.data.propertyUtility.vendorId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This property does not have a vendor assigned. Open the Utilities card and select a vendor before saving utility configuration.
        </Alert>
      )}

      <Divider className="mb-3 mt-3" />
      <Box sx={{ display: 'flex', gap: 3, width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Property Info Card - 50% width */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' }, minWidth: 0 }}>
          <PropertyDetailCard />
        </Box>
        
        {/* Utilities Card - 50% width */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' }, minWidth: 0 }}>
          <PropertyUtilityDetailCard />
        </Box>
      </Box>
      

        

          <PropertyDashboardTabedTables
            propertyUuid={params.uuid}
            propertyId={params.id}
          />



      {/* Property Manager Modal */}
      <Dialog 
        open={propertyManagerModalOpen} 
        onClose={() => setPropertyManagerModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Property Manager Details
        </DialogTitle>
        <DialogContent>
          <PropertyManagerDetailCard />
        </DialogContent>
      </Dialog>

      {/* Accounting ID Modal */}
      <PropertyGLCodeDetailCard 
        hideCard={true}
        externalOpen={accountingIdModalOpen}
        onExternalClose={() => setAccountingIdModalOpen(false)}
      />
    </NavFrame>
  );
};

IndividualPropertyPage.propTypes = {
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
  units: PropTypes.object.isRequired,
  propertyGLCode: PropTypes.object.isRequired,
  unitTypes: PropTypes.object.isRequired,
  propertyUtilities: PropTypes.object.isRequired,
  propertyManagers: PropTypes.object.isRequired,

  getUnits: PropTypes.func.isRequired,
  getPropertyByUuid: PropTypes.func.isRequired,
  getPropertyUtilityByPropertyId: PropTypes.func.isRequired,
  getPropertyGLCodeByPropertyId: PropTypes.func.isRequired,
  getPropertyManagerByUuid: PropTypes.func.isRequired,
  getOwners: PropTypes.func.isRequired,
  getVendors: PropTypes.func.isRequired,
  getDevelopers: PropTypes.func.isRequired,
  getPropertyManagers: PropTypes.func.isRequired,
  getPropertyStatuses: PropTypes.func.isRequired,
  getPropertyManagerStatuses: PropTypes.func.isRequired,
  getPropRiderRatesByPropByUuid: PropTypes.func.isRequired,
  getAllPropertyPreviews: PropTypes.func.isRequired,
  getAllAccountStatusCodes: PropTypes.func.isRequired,
  getAllCustomerTypes: PropTypes.func.isRequired,
  getAllCommonChargeRelatedDataByPropertyId: PropTypes.func.isRequired,
  getAllUnitTypes: PropTypes.func.isRequired,
};

// Component State
function IndividualPropertyPageState(state) {
  return {
    user: state.user,
    apl: state.apl,
    properties: state.properties,
    units: state.units,
    propertyGLCode: state.propertyGLCode,
    unitTypes: state.unitTypes,
    propertyUtilities: state.propertyUtilities,
    propertyManagers: state.propertyManagers,
  };
}

export default connect(IndividualPropertyPageState, {
  getUnitsByPropertyId,
  getPropertyByUuid,
  getPropertyUtilityByPropertyId,
  getPropertyManagerById,
  getOwners,
  getVendors,
  getDevelopers,
  getPropertyManagers,
  getPropertyStatuses,
  getPropertyManagerStatuses,
  getAllUnitTypes,
  getPropRiderRatesByPropByUuid,
  getResolvedRiderRatesByPropertyUuid,
  getPropertyGLCodeByPropertyId,
  getAllPropertyPreviews,
  getAllAccountStatusCodes,
  getAllCustomerTypes,
  getAllCommonChargeRelatedDataByPropertyId,
})(IndividualPropertyPage);
